import { memo, useCallback } from 'react';
import { Button, DatatableCell } from '@monksflow/monks-ui';
import { Trash2Icon } from 'lucide-react';
import { Table, TableRow } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { COLUMNS } from './WorkspaceWhitelistRulesTable.constants';
import type { TablePaginationConfig } from '@/shared/ui';
import { type ModelProviderWhitelistResponseDto } from '@inference.engine/api-client';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export interface WorkspaceRule {
  workspaceId: string;
  workspaceName: string;
  providers: ModelProviderWhitelistResponseDto[];
}

type SortableWorkspaceRuleKeys = 'workspaceName';

interface WorkspaceWhitelistRulesTableProps {
  rules: WorkspaceRule[];
  isLoading?: boolean;
  hasSearch: boolean;
  pagination?: TablePaginationConfig;
  sortBy?: keyof WorkspaceRule;
  sortDirection?: 'asc' | 'desc';
  onSort?: (property: SortableWorkspaceRuleKeys, direction: 'asc' | 'desc') => void;
  onDelete?: (workspaceId: string, workspaceName: string) => void;
}

interface RenderBodyRowProps {
  row: WorkspaceRule;
  onDelete?: (workspaceId: string, workspaceName: string) => void;
  t: TFunction<'admin'>;
}

const renderBodyRow = ({ row, onDelete, t }: RenderBodyRowProps) => (
  <TableRow key={row.workspaceId} hover>
    <DatatableCell>
      <span className="text-sm leading-5">{row.workspaceName}</span>
    </DatatableCell>
    <DatatableCell>
      <div className="flex items-center justify-between">
        <span className="text-sm leading-5">{row.providers.map((p) => p.name).join(' • ')}</span>
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('admin.workspaceWhitelistRulesTable.deleteRuleFor', {
              workspaceName: row.workspaceName,
            })}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.workspaceId, row.workspaceName);
            }}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2Icon className="size-5" />
          </Button>
        )}
      </div>
    </DatatableCell>
  </TableRow>
);

export const WorkspaceWhitelistRulesTable = memo(
  ({
    rules,
    isLoading = false,
    hasSearch,
    pagination,
    sortBy = 'workspaceName',
    sortDirection = 'asc',
    onSort,
    onDelete,
  }: WorkspaceWhitelistRulesTableProps) => {
    const { t } = useTranslation('admin');

    const tableIsEmpty = rules.length === 0 && !isLoading;

    const handleSort = useCallback(
      (property: keyof WorkspaceRule) => {
        if (!onSort) return;

        const sortableKeys: SortableWorkspaceRuleKeys[] = ['workspaceName'];
        if (sortableKeys.includes(property as SortableWorkspaceRuleKeys)) {
          // eslint-disable-next-line i18next/no-literal-string -- sort direction state values
          const newDirection = sortBy === property && sortDirection === 'asc' ? 'desc' : 'asc';
          onSort(property as SortableWorkspaceRuleKeys, newDirection);
        }
      },
      [onSort, sortBy, sortDirection],
    );

    if (tableIsEmpty) {
      return (
        <div className="w-full p-4">
          <EmptyState
            title={
              hasSearch
                ? t('admin.workspaceWhitelistRulesTable.noRulesMatchYourSearch')
                : t('admin.workspaceWhitelistRulesTable.noWhitelistRuleAddedYet')
            }
            description={
              hasSearch
                ? t('admin.workspaceWhitelistRulesTable.tryAdjustingYourSearchTerm')
                : t('admin.workspaceWhitelistRulesTable.clickOnAddRuleTo')
            }
            height="400px"
          />
        </div>
      );
    }

    return (
      <Table
        isLoading={isLoading}
        columns={COLUMNS}
        sortBy={sortBy}
        sortDirection={sortDirection}
        rows={rules}
        pagination={{
          count: pagination?.count || 0,
          rowsPerPage: pagination?.rowsPerPage || 10,
          page: pagination?.page || 0,
          onPageChange: pagination?.onPageChange || (() => {}),
          onRowsPerPageChange: pagination?.onRowsPerPageChange || (() => {}),
        }}
        renderBodyRow={(row) => renderBodyRow({ row, onDelete, t })}
        onSort={handleSort}
        containerClassName="px-0 pb-0"
      />
    );
  },
);

WorkspaceWhitelistRulesTable.displayName = 'WorkspaceWhitelistRulesTable';
